(function () {
  'use strict';

  class CartBarElement extends HTMLElement {
    constructor() {
      super();
      this.section = this.closest('product-info');
      this.form = this.section?.querySelector('[data-product-form]');
      this.formWrapper =
        (window.theme?.settings?.productPageSticky && this.section?.querySelector('[data-form-wrapper]')) ||
        this.form;
      this.footer = null;
      this.handleProductAddError = this.handleProductAddError.bind(this);
      this.boundToggleOnScroll = this.toggleCartBarOnScroll.bind(this);
      this.boundSetHeight = this.setCartBarHeight.bind(this);
      this.boundOnPcTotal = this.onPcTotal.bind(this);
    }

    connectedCallback() {
      this.setCartBarHeight();
      this.addEventListener('click', this.handleClick.bind(this));
      document.addEventListener('theme:scroll', this.boundToggleOnScroll);
      document.addEventListener('theme:resize', this.boundSetHeight);
      document.addEventListener('theme:product:add-error', this.handleProductAddError);

      // Print configurator total (bubbles up through product-info)
      this.section?.addEventListener('print-configurator:total', this.boundOnPcTotal);

      // Initial sync — if the print-configurator dispatched its first total
      // (from URL params) BEFORE this listener was attached, we'd miss it
      // and the cart-bar would show only the bare variant price with no addons.
      // Ask the configurator to re-dispatch once on the next tick.
      requestAnimationFrame(() => {
        const pc = this.section?.querySelector('print-configurator');
        if (pc && typeof pc.dispatchTotal === 'function') {
          pc.dispatchTotal();
        }
      });

      // Variant changes for price + label updates
      if (window.subscribe && window.theme?.PUB_SUB_EVENTS?.variantChange) {
        this._variantUnsub = window.subscribe(window.theme.PUB_SUB_EVENTS.variantChange, (data) => {
          this.onVariantChange(data);
        });
      }
    }

    disconnectedCallback() {
      document.removeEventListener('theme:product:add-error', this.handleProductAddError);
      document.removeEventListener('theme:scroll', this.boundToggleOnScroll);
      document.removeEventListener('theme:resize', this.boundSetHeight);
      this.section?.removeEventListener('print-configurator:total', this.boundOnPcTotal);
      this._variantUnsub?.();
    }

    handleClick(e) {
      if (e.target.closest('[data-cart-bar-scroll]')) {
        e.preventDefault();
        this.scrollToTop();
      }
    }

    handleProductAddError() {
      this.scrollToTop();
    }

    scrollToTop() {
      const isMobile = window.innerWidth < 1024;
      const target = this.section?.querySelector('variant-selects') || this.form;
      const el = isMobile ? target : this.section;
      const top = el?.getBoundingClientRect().top ?? 0;
      window.theme?.scrollTo(isMobile ? top - 10 : top);
    }

    toggleCartBarOnScroll() {
      if (!this.formWrapper) return;

      // Show once the product form has scrolled out of view above the viewport.
      const formOutOfView = this.formWrapper.getBoundingClientRect().bottom <= 0;

      // Hide again once the footer reaches the viewport, so the bar doesn't
      // overlap the footer at the bottom of the page.
      const footer = this.footer || (this.footer = document.querySelector('[data-section-type="footer"]'));
      const footerReached = footer ? footer.getBoundingClientRect().top <= window.innerHeight : false;

      this.classList.toggle('is-visible', formOutOfView && !footerReached);
    }

    setCartBarHeight() {
      document.documentElement.style.setProperty('--cart-bar-height', `${this.offsetHeight}px`);
    }

    formatPrice(cents) {
      const fmt = window.theme?.formatMoney;
      const moneyFormat = window.theme?.moneyFormat;
      return fmt ? fmt(cents, moneyFormat) : (cents / 100).toFixed(2);
    }

    onPcTotal(e) {
      // Update the single price display with the full total
      const priceEl = this.querySelector('[data-cart-bar-price]');
      if (priceEl && typeof e.detail?.cents === 'number') {
        priceEl.innerHTML = this.formatPrice(e.detail.cents);
      }

      const isUnavailable = !!e.detail?.isUnavailable;

      // Populate the add-ons list from the configurator's selection
      const pc = this.section?.querySelector('print-configurator');
      const addonsList = this.querySelector('[data-cart-bar-addons]');
      if (!pc || !addonsList) return;

      // When the selected size is sold out, hide the add-ons list — the
      // user can't buy this combination, and the cart-bar should reflect
      // only the unavailable selection.
      if (isUnavailable) {
        addonsList.innerHTML = '';
        addonsList.hidden = true;

        // Also reflect the selected (sold-out) size in the cart-bar variant label.
        const selectedOpt = pc.querySelector('[data-pc-option][aria-selected="true"]');
        const variantEl = this.querySelector('[data-cart-bar-variant]');
        if (variantEl && selectedOpt?.dataset.size) {
          variantEl.textContent = selectedOpt.dataset.size;
          variantEl.hidden = false;
        }
        return;
      }

      // Available: keep variant label in sync with current selection too.
      const selectedOpt = pc.querySelector('[data-pc-option][aria-selected="true"]');
      const variantEl = this.querySelector('[data-cart-bar-variant]');
      if (variantEl && selectedOpt?.dataset.size) {
        variantEl.textContent = selectedOpt.dataset.size;
        variantEl.hidden = false;
      }

      const addons = [];
      if (pc.selection?.frame) {
        addons.push({ role: 'Frame', value: pc.selection.frame.productTitle });
      }
      if (pc.selection?.passepartout) {
        addons.push({ role: 'Passepartout', value: pc.selection.passepartout.productTitle });
      }

      if (addons.length > 0) {
        addonsList.innerHTML = addons
          .map(
            (a) =>
              `<li><span class="cart-bar__addon-role">${a.role}</span><span class="cart-bar__addon-value">${a.value}</span></li>`
          )
          .join('');
        addonsList.hidden = false;
      } else {
        addonsList.innerHTML = '';
        addonsList.hidden = true;
      }
    }

    onVariantChange(data) {
      // Update variant label
      const variantEl = this.querySelector('[data-cart-bar-variant]');
      if (variantEl && data?.variant) {
        const title = data.variant.title;
        if (!title || title === 'Default Title') {
          variantEl.hidden = true;
        } else {
          variantEl.textContent = title;
          variantEl.hidden = false;
        }
      }

      // Update Add to cart / Sold out button state for the selected variant
      const btn = this.querySelector('button.product__submit__add');
      const btnText = this.querySelector('[data-cart-bar-button-text]');
      if (btn && btnText) {
        const available = !!data?.variant?.available;
        const labelAdd = window.theme?.strings?.addToCart || btn.dataset.labelAdd || 'Add to cart';
        const labelSoldOut = window.theme?.strings?.soldOut || btn.dataset.labelSoldOut || 'Sold out';
        btnText.textContent = available ? labelAdd : labelSoldOut;
        if (available) {
          btn.removeAttribute('disabled');
          btn.setAttribute('data-cart-bar-scroll', '');
        } else {
          btn.setAttribute('disabled', 'disabled');
          btn.removeAttribute('data-cart-bar-scroll');
        }
      }

      // Update price — only when no configurator (configurator fires dispatchTotal on variant change)
      const pc = this.section?.querySelector('print-configurator');
      if (!pc && data?.variant?.price != null) {
        const priceEl = this.querySelector('[data-cart-bar-price]');
        if (priceEl) priceEl.innerHTML = this.formatPrice(data.variant.price);
      }
    }
  }

  if (!customElements.get('cart-bar')) {
    customElements.define('cart-bar', CartBarElement);
  }
})();
